import type { Baermann } from '@/features/analisis/baermann/types';
import type { Coprocultivo } from '@/features/analisis/coprocultivo/types';
import type { Ectoparasitos } from '@/features/analisis/ectoparasitos/types';
import type { Hpg } from '@/features/analisis/hpg/types';
import type { Opg } from '@/features/analisis/opg/types';
import type { SedimentacionFh } from '@/features/analisis/sedimentacion-fh/types';
import type { TipoAnalisis } from '@/types/enums';

export type ComplementarioVinculado = Opg | Coprocultivo | Baermann | SedimentacionFh;

export interface GrupoHpg {
  tipoGrupo: 'hpg';
  hpg: Hpg;
  complementarios: ComplementarioVinculado[];
  fechaOrden: string;
}

export type TipoIndividual =
  | typeof TipoAnalisis.COPROCULTIVO
  | typeof TipoAnalisis.BAERMANN
  | typeof TipoAnalisis.SEDIMENTACION_FH
  | typeof TipoAnalisis.ECTOPARASITOS;

export interface GrupoIndividual {
  tipoGrupo: 'individual';
  tipo: TipoIndividual;
  analisis: Coprocultivo | Baermann | SedimentacionFh | Ectoparasitos;
  fechaOrden: string;
}

export type GrupoAnalisis = GrupoHpg | GrupoIndividual;

export function agruparAnalisis(
  hpgs: Hpg[],
  opgs: Opg[],
  coprocultivos: Coprocultivo[],
  baermanns: Baermann[],
  sedimentacionesFh: SedimentacionFh[],
  ectoparasitos: Ectoparasitos[],
): GrupoAnalisis[] {
  const grupos: GrupoAnalisis[] = [];

  hpgs.forEach((hpg) => {
    const uid = hpg.uid_analisis;
    const complementarios: ComplementarioVinculado[] = [
      ...opgs.filter((x) => x.uid_analisis_hpg_referencia === uid),
      ...coprocultivos.filter((x) => x.uid_analisis_hpg_referencia === uid),
      ...baermanns.filter((x) => x.uid_analisis_hpg_referencia === uid),
      ...sedimentacionesFh.filter((x) => x.uid_analisis_hpg_referencia === uid),
    ];
    grupos.push({ tipoGrupo: 'hpg', hpg, complementarios, fechaOrden: hpg.fecha_creacion });
  });

  coprocultivos
    .filter((x) => !x.uid_analisis_hpg_referencia)
    .forEach((c) =>
      grupos.push({
        tipoGrupo: 'individual',
        tipo: 'coprocultivo',
        analisis: c,
        fechaOrden: c.fecha_creacion,
      }),
    );

  baermanns
    .filter((x) => !x.uid_analisis_hpg_referencia)
    .forEach((b) =>
      grupos.push({
        tipoGrupo: 'individual',
        tipo: 'baermann',
        analisis: b,
        fechaOrden: b.fecha_creacion,
      }),
    );

  sedimentacionesFh
    .filter((x) => !x.uid_analisis_hpg_referencia)
    .forEach((s) =>
      grupos.push({
        tipoGrupo: 'individual',
        tipo: 'sedimentacion_fh',
        analisis: s,
        fechaOrden: s.fecha_creacion,
      }),
    );

  ectoparasitos.forEach((e) =>
    grupos.push({
      tipoGrupo: 'individual',
      tipo: 'ectoparasitos',
      analisis: e,
      fechaOrden: e.fecha_creacion,
    }),
  );

  return grupos.sort(
    (a, b) => new Date(a.fechaOrden).getTime() - new Date(b.fechaOrden).getTime(),
  );
}
